import { createBoardDoc, SceneStore, toBoardFile, type BoardFile, type NewObject } from '@os/doc';
import type { TemplateCategory } from '../../lib/api';
import { DEFAULT_STICKY } from '../../ui/palette';

export type SystemTemplateId = 'blank' | 'moodboard' | 'references' | 'presentation';
export type FilledTemplateId = Exclude<SystemTemplateId, 'blank'>;

/** The four system templates of spec §8, in gallery order; the blank board has no category. */
export const SYSTEM_TEMPLATES: readonly { id: SystemTemplateId; category: TemplateCategory | null }[] = [
  { id: 'blank', category: null },
  { id: 'moodboard', category: 'moodboard' },
  { id: 'references', category: 'references' },
  { id: 'presentation', category: 'presentation' },
];

export const LABEL_KEYS = [
  'palette', 'typography', 'references', 'mood', 'notes', 'headingSample', 'bodySample', 'noteSample',
  'inspiration', 'materials', 'colours', 'dropImage',
  'cover', 'coverTitle', 'coverSubtitle', 'studioWord', 'studioWordBody', 'direction1', 'direction2', 'nextSteps',
] as const;
export type TemplateLabels = Record<(typeof LABEL_KEYS)[number], string>;

/** The template texts in the user's language (`boardTemplates.labels.*`). */
export function templateLabels(t: (key: string) => string): TemplateLabels {
  return Object.fromEntries(LABEL_KEYS.map((k) => [k, t(`boardTemplates.labels.${k}`)])) as TemplateLabels;
}

const PALETTE = ['#1f1d1a', '#8a847b', '#d9d4cc', '#f6f3ee', '#c4553a'];
const PLACEHOLDER = '#ece8e1';
const STICKY = DEFAULT_STICKY;
const EXPORTED_AT = '2026-11-02T00:00:00.000Z';

type Box = { x: number; y: number; w: number; h: number };
type Builder = { section(name: string, box: Box, clip: boolean): string; add(data: NewObject): void };
const common = (parentId: string | null, b: Box) => ({ parentId, ...b, rotation: 0, locked: false, style: {} });

function build(title: string, fill: (b: Builder) => void): BoardFile {
  const store = new SceneStore(createBoardDoc(), { captureTimeout: 0 });
  fill({
    section: (name, box, clip) =>
      store.commands.createObject({ ...common(null, box), type: 'section', name, fill: clip ? '#ffffff' : '#f4f4f5', clip, collapsed: false }),
    add: (data) => void store.commands.createObject(data),
  });
  return toBoardFile(store.doc, { title, exportedAt: EXPORTED_AT, assets: [] });
}

const text = (parentId: string, b: Box, value: string, fontSize: number): NewObject => ({ ...common(parentId, b), type: 'text', text: value, fontSize });

function moodboard(l: TemplateLabels): BoardFile {
  return build(l.palette, (b) => {
    const at = (x: number, y: number, w = 800): Box => ({ x, y, w, h: 600 });
    const palette = b.section(l.palette, at(0, 0), true);
    PALETTE.forEach((fill, i) => b.add({ ...common(palette, { x: 40 + i * 150, y: 80, w: 120, h: 120 }), style: { fill }, type: 'shape', shape: 'rectangle' }));
    const typo = b.section(l.typography, at(880, 0), true);
    b.add(text(typo, { x: 40, y: 80, w: 720, h: 72 }, l.headingSample, 48));
    b.add(text(typo, { x: 40, y: 200, w: 720, h: 48 }, l.bodySample, 24));
    b.section(l.references, at(1760, 0), true);
    b.section(l.mood, at(0, 680, 1680), true);
    const notes = b.section(l.notes, at(1760, 680), true);
    for (let i = 0; i < 3; i++) b.add({ ...common(notes, { x: 40 + i * 240, y: 80, w: 200, h: 200 }), type: 'sticky', text: l.noteSample, color: STICKY });
  });
}

function references(l: TemplateLabels): BoardFile {
  return build(l.inspiration, (b) => {
    [l.inspiration, l.materials, l.colours].forEach((name, i) => {
      const s = b.section(name, { x: 0, y: i * 840, w: 1040, h: 720 }, false);
      for (let r = 0; r < 2; r++)
        for (let c = 0; c < 3; c++)
          b.add({ ...common(s, { x: 40 + c * 340, y: 80 + r * 265, w: 300, h: 225 }), style: { fill: PLACEHOLDER }, type: 'shape', shape: 'rectangle', text: l.dropImage });
    });
  });
}

function presentation(l: TemplateLabels): BoardFile {
  return build(l.cover, (b) => {
    const frame = (name: string, i: number) => b.section(name, { x: i * 2080, y: 0, w: 1920, h: 1080 }, true);
    const cover = frame(l.cover, 0);
    b.add(text(cover, { x: 160, y: 400, w: 1600, h: 120 }, l.coverTitle, 96));
    b.add(text(cover, { x: 160, y: 560, w: 1600, h: 60 }, l.coverSubtitle, 40));
    const word = frame(l.studioWord, 1);
    b.add(text(word, { x: 160, y: 160, w: 1600, h: 80 }, l.studioWord, 64));
    b.add(text(word, { x: 160, y: 320, w: 1600, h: 400 }, l.studioWordBody, 32));
    [l.direction1, l.direction2, l.nextSteps].forEach((name, i) => b.add(text(frame(name, i + 2), { x: 160, y: 160, w: 1600, h: 80 }, name, 64)));
  });
}

/** A system template as a `BoardFile v1` (plan P10 Ruling 2): built through a fresh store, so ids and z are the store's. */
export function systemTemplate(id: FilledTemplateId, labels: TemplateLabels): BoardFile {
  return id === 'moodboard' ? moodboard(labels) : id === 'references' ? references(labels) : presentation(labels);
}
